import { Box, Button, Grid, IconButton } from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import { FieldArray } from "formik";
import { useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import * as yup from "yup";
import {
  AdviceDelivery,
  AdviceStatuses,
  ApplicationClientRoles,
  ApplicationDto,
  InvestmentOptions,
  Product,
  validDriverLicenceSchema,
  validPassportSchema,
  WealthBuilderApplicationDto,
} from "../../api";
import { initialValuesApplicationDefaults, isBnzBankAccount } from "../../common/helpers";
import {
  ApplicationClient,
  Client,
  distributionOptionOptions,
  incomeBands,
  IncomeGeneratorApplication,
  investmentMethodOptions,
  investmentPurposeOptions,
  ScreenProps,
  SendApplicationToClientResult,
  wealthBands,
  WealthBuilderApplication,
  yesNoOptions,
} from "../../common/types";
import { useAppDispatch, useAppSelector } from "../../store";
import { clearClient, clearUpsertClientResult, getClientAsync, saveClient, setMessage, upsertClientAsync } from "../../store/adviser";
import { hasSelectedProductRoles } from "../adviser/Helpers";
import FormikWrapper from "../containers/FormikWrapper";
import { AlertMessage, Colors, ErrorMessage, FormLabel, ProductIcon, SectionTitleText, SubtitleText, TitleText } from "../controls/helpers";
import { Checkbox, InvestmentOption, RadioList, Select, TextInput } from "../controls/inputs";
import SelectIdentification, { getIdentificationOption, identificationOptions } from "./SelectIdentification";
import SendApplicationToClient from "./SendApplicationToClient";

enum FormMode {
  None,
  Confirm,
  Submit,
  Send,
}

enum ApplicationType {
  Individual = "Individual",
  Joint = "Joint",
}

const allocationSchema = (path: string) =>
  yup.object().test("100% allocated", "Total allocation must be 100%", function (values: any) {
    return (
      values.investmentOption !== InvestmentOptions.SelfSelect ||
      (Number(values["income"]) || 0) + (Number(values["inflation"]) || 0) + (Number(values["growth"]) || 0) === 100 ||
      this?.createError({ path, message: "Total allocation must be 100%" })
    );
  });

const applicationValidationSchema = (primaryEmail: string) =>
  yup.object({
    applicationClients: yup.array(
      yup.object({
        overMinAge: yup.boolean().when("role", {
          is: ApplicationClientRoles.Joint,
          then: yup.boolean().oneOf([true], "Confirm joint applicant is over 16 years"),
        }),
        firstName: yup.string().when("role", (role: ApplicationClientRoles) => (role === ApplicationClientRoles.Joint ? yup.string().required("First name is required") : yup.string())),
        lastName: yup.string().when("role", (role: ApplicationClientRoles) => (role === ApplicationClientRoles.Joint ? yup.string().required("Last name is required") : yup.string())),
        email: yup.string().when("role", {
          is: ApplicationClientRoles.Joint,
          then: yup.string().email("Email is invalid").required("Email is required").notOneOf([primaryEmail], "Joint applicant must have their own email"),
        }),
        mobile: yup.string(),
      })
    ),
  });

const validationSchema = (client?: Client) =>
  yup.lazy(() => {
    return yup
      .object({
        kiwiSaverApplication: applicationValidationSchema(client?.email || "").concat(allocationSchema("ksTotalAllocated")),
        wealthBuilderApplications: yup.array(applicationValidationSchema(client?.email || "").concat(allocationSchema("wbTotalAllocated"))),
        incomeGeneratorApplications: yup.array(applicationValidationSchema(client?.email || "")),
        passport: validPassportSchema,
        driverLicence: validDriverLicenceSchema,
      })
      .test("Duplicate joint applicant email", "Different first/last name but the same email", function (values: Client) {
        const wbJointApplications: ApplicationDto[] | undefined = values.wealthBuilderApplications?.filter((a) => a.applicationClients?.some((client) => client.role === ApplicationClientRoles.Joint));
        const igJointApplications: ApplicationDto[] | undefined = values.incomeGeneratorApplications?.filter((a) =>
          a.applicationClients?.some((client) => client.role === ApplicationClientRoles.Joint)
        );

        if (!wbJointApplications?.length || !igJointApplications?.length) return true;

        const applications: ApplicationDto[] = [...wbJointApplications, ...igJointApplications];
        const applicationClients = applications.map((a) => a.applicationClients || []).reduce((a, b) => a.concat(b));
        const jointClients = applicationClients.filter((ac) => ac.role === ApplicationClientRoles.Joint).map((ac) => ac);
        const hasDuplicatedEmail = jointClients.some((client, index) =>
          jointClients.some(
            (c, i) =>
              index !== i &&
              (c.firstName?.toLowerCase() !== client.firstName?.toLowerCase() || c.lastName?.toLowerCase() !== client.lastName?.toLowerCase()) &&
              c.email?.toLowerCase() === client.email?.toLowerCase()
          )
        );

        return !hasDuplicatedEmail || this?.createError({ path: "jointApplicant", message: "Joint applicants must have a unique email" });
      });
  });

const isJointAccount = (application: ApplicationDto): boolean => !!application.applicationClients?.some((client) => client.role === ApplicationClientRoles.Joint);

const initializeApplication = <T extends WealthBuilderApplication & IncomeGeneratorApplication>(application: T): WealthBuilderApplication & IncomeGeneratorApplication => {
  return initialValuesApplicationDefaults({
    ...application,
    applicationClients: application.applicationClients?.map((a) => ({ ...a, overMinAge: !!a.clientId || a.overMinAge })),
  });
};

const ProductDetails = ({ match, nextRoute, prevRoute, history }: RouteComponentProps & ScreenProps) => {
  const dispatch = useAppDispatch();
  const { client, upsertClientResult } = useAppSelector((state) => state.adviser);
  const [formMode, setFormMode] = useState<FormMode>(FormMode.None);
  const [prevScreen, setPrevScreen] = useState(false);
  const clientId = (match.params as { clientId: string }).clientId;
  const showInvestmentOptions = client?.adviceStatus === AdviceStatuses.Skipped;
  const { passportAndDriverLicence, passport, driverLicence } = identificationOptions();
  const { user } = useAppSelector((state) => state.oidc);

  const initialValues = useCallback(
    () => ({
      jointApplicant: undefined,
      ksTotalAllocated: undefined,
      wbTotalAllocated: undefined,
      ...client,
      kiwiSaverApplication: client?.kiwiSaverApplication && { isWithAnotherProvider: undefined, isEligible: undefined, investmentOption: undefined, ...client?.kiwiSaverApplication },
      wealthBuilderApplications: client?.wealthBuilderApplications?.map((wb) => initializeApplication(wb)) || [],
      incomeGeneratorApplications: client?.incomeGeneratorApplications?.map((ig) => initializeApplication(ig)) || [],
      rego: !!client?.rego ? { numberPlate: "", ...client?.rego } : undefined,
      passport: !!client?.passport ? { passportNumber: "", expiryDate: undefined, ...client?.passport } : undefined,
      driverLicence: !!client?.driverLicence ? { licenceNumber: "", versionNumber: "", ...client?.driverLicence } : undefined,
      identificationOption: getIdentificationOption(false, false, client),
    }),
    [client]
  );

  const cleanValues = useCallback(
    (values: Client) => ({
      ...values,
      kiwiSaverApplication:
        values.kiwiSaverApplication?.isEligible === false || (!values.products?.includes(Product.KiwiSaver) && !client?.kiwiSaverApplication) ? undefined : { ...values?.kiwiSaverApplication },
      wealthBuilderApplications: values.wealthBuilderApplications?.map((wb) => ({ ...wb, income: wb.income || undefined, inflation: wb.inflation || undefined, growth: wb.growth || undefined })) || [],
      passport: values.identificationOption === passport || values.identificationOption === passportAndDriverLicence ? { passportNumber: "", expiryDate: undefined, ...values.passport } : undefined,
      driverLicence:
        values.identificationOption === driverLicence || values.identificationOption === passportAndDriverLicence ? { licenceNumber: "", versionNumber: "", ...values.driverLicence } : undefined,
    }),
    [client, driverLicence, passport, passportAndDriverLicence]
  );

  const openSendToClient = useCallback(
    (values: Client) => {
      dispatch(saveClient(cleanValues(values)));
      setFormMode(FormMode.Confirm);
    },
    [cleanValues, dispatch]
  );

  const sendToClient = useCallback(
    (result: SendApplicationToClientResult) => {
      dispatch(upsertClientAsync({ ...client, ...result }));
      setFormMode(FormMode.Send);
    },
    [client, dispatch]
  );

  const handleSubmit = useCallback(
    async (values: Client) => {
      const _values = cleanValues(values);
      dispatch(saveClient(_values));

      if (formMode === FormMode.None) {
        if (values?.adviceDelivery === AdviceDelivery.InPerson) {
          dispatch(upsertClientAsync(_values));
          setFormMode(FormMode.Submit);
        } else prevScreen ? prevRoute?.(_values) : nextRoute(_values);
      }
    },
    [cleanValues, dispatch, formMode, nextRoute, prevRoute, prevScreen]
  );

  useEffect(() => {
    if (formMode === FormMode.Submit) {
      if (upsertClientResult?.clientId) {
        (async () => {
          dispatch(clearUpsertClientResult());
          const result = await dispatch(getClientAsync(clientId)).unwrap();
          setFormMode(FormMode.None);
          prevScreen ? prevRoute?.(result) : nextRoute(result);
        })();
      }
    } else if (formMode === FormMode.Send) {
      if (upsertClientResult?.clientId) {
        setFormMode(FormMode.None);
        dispatch(clearClient());
        dispatch(setMessage({ text: "The application has been sent successfully." }));
        history.replace(`/adviser/${clientId}/client-application`);
      }
    }
  }, [client, clientId, formMode, upsertClientResult]);

  return (
    <>
      <FormikWrapper initialValues={initialValues()} validationSchema={validationSchema(client)} onSubmit={handleSubmit}>
        {({ errors, isValid, setFieldValue, values }) => (
          <>
            <Box mb={3}>
              <TitleText hideMarginBottom>Products</TitleText>
              <SubtitleText hideMarginBottom>
                <Grid container justifyContent="space-between">
                  <Grid item style={{ paddingTop: 3 }}>
                    <span style={{ color: Colors.Black }}>
                      {values.firstName} {values.lastName || ""}{" "}
                    </span>
                    &emsp;{values.adviceStatus === AdviceStatuses.Skipped ? "Product onboarding" : "Savings Advice Tool"}
                  </Grid>
                  <Grid item>
                    {values.products?.includes(Product.KiwiSaver) && <ProductIcon product={Product.KiwiSaver} highlighted />}
                    {values.products?.includes(Product.WealthBuilder) && <ProductIcon product={Product.WealthBuilder} highlighted />}
                    {values.products?.includes(Product.IncomeGenerator) && <ProductIcon product={Product.IncomeGenerator} highlighted />}
                  </Grid>
                </Grid>
              </SubtitleText>
            </Box>
            {values.products?.includes(Product.KiwiSaver) && (
              <Box mb={3}>
                <SectionTitleText hideMarginTop>KiwiSaver</SectionTitleText>
                <Grid container spacing={3}>
                  <Grid item md={6} xs={12}>
                    <RadioList
                      horizontal
                      name="kiwiSaverApplication.isWithAnotherProvider"
                      label="Is the client already in KiwiSaver?"
                      options={yesNoOptions}
                      onChange={(alreadyInKiwiSaver) => alreadyInKiwiSaver && setFieldValue("kiwiSaverApplication.isEligible", undefined)}
                    />
                  </Grid>
                  {values.kiwiSaverApplication!.isWithAnotherProvider === false && (
                    <Grid item md={6} xs={12}>
                      <RadioList
                        horizontal
                        name="kiwiSaverApplication.isEligible"
                        label="Is the client eligible to join KiwiSaver?"
                        options={yesNoOptions}
                        tooltip="KiwiSaver is open to all New Zealand citizens and people entitled to be in New Zealand indefinitely. A person needs to be living (or normally living) in New Zealand to join. You cannot join KiwiSaver if you have a temporary, visitor, work or student visa."
                      />
                    </Grid>
                  )}
                  {values.kiwiSaverApplication!.isEligible === false ? (
                    <Grid item xs={12}>
                      <AlertMessage
                        error
                        hideClose
                        message={`${
                          values.products?.includes(Product.WealthBuilder) || values.products?.includes(Product.IncomeGenerator)
                            ? "KiwiSaver will be removed as the client is not eligible. Proceed with your other product selections"
                            : "The client is not eligible to apply for KiwiSaver. You will not be able to proceed"
                        }.`}
                      />
                    </Grid>
                  ) : (
                    showInvestmentOptions && (
                      <Grid item xs={12}>
                        <FormLabel label="Investment options" />
                        <InvestmentOption horizontal namePrefix="kiwiSaverApplication" kiwiSaver value={values.kiwiSaverApplication} showBalancedOption validationKey="ksTotalAllocated" />
                      </Grid>
                    )
                  )}
                </Grid>
              </Box>
            )}
            {values.products?.includes(Product.WealthBuilder) && (
              <FieldArray
                name="wealthBuilderApplications"
                render={(arrayHelpers) =>
                  values.wealthBuilderApplications
                    .filter((a) => a.active)
                    .map((application, index) => {
                      const key = `wealthBuilderApplications[${index}]`;
                      const isJoint = isJointAccount(application);
                      const applicationClientIndex = application.applicationClients?.findIndex((client) => client.role === ApplicationClientRoles.Joint);
                      const otherSelected =
                        application.investmentPurpose === "" || (!!application.investmentPurpose && !investmentPurposeOptions.find((p) => p.value === application.investmentPurpose));

                      const updateApplicationClients = (applicationType: ApplicationType) => {
                        if (applicationType === ApplicationType.Individual) {
                          setFieldValue(
                            `${key}.applicationClients`,
                            application.applicationClients?.filter((client) => client.role === ApplicationClientRoles.Primary)
                          );
                          setFieldValue(`${key}.annualIncome`, client?.annualIncome);
                          setFieldValue(`${key}.estimatedTotalWealth`, client?.estimatedTotalWealth);
                        } else {
                          setFieldValue(
                            `${key}.applicationClients`,
                            ((application.applicationClients || []) as ApplicationClient[]).concat({
                              role: ApplicationClientRoles.Joint,
                              firstName: "",
                              middleName: "",
                              lastName: "",
                              mobile: "",
                              email: "",
                              overMinAge: false,
                            })
                          );
                          setFieldValue(`${key}.annualIncome`, undefined);
                          setFieldValue(`${key}.estimatedTotalWealth`, undefined);
                        }
                      };

                      return (
                        <Box mb={3} key={application.applicationId || index}>
                          <SectionTitleText>
                            <Grid container justifyContent="space-between">
                              <Grid item>{`Wealth Builder${application.nickname && ` - ${application.nickname}`}`}</Grid>
                              {index > 0 && (
                                <Grid item>
                                  <IconButton color="inherit" size="small" onClick={(_) => arrayHelpers.remove(index)}>
                                    <ClearIcon />
                                  </IconButton>
                                </Grid>
                              )}
                            </Grid>
                          </SectionTitleText>
                          <Grid container spacing={3}>
                            <Grid item md={6} xs={12}>
                              <RadioList
                                horizontal
                                label="Account type"
                                name={`${key}.applicationType`}
                                value={isJoint ? ApplicationType.Joint : ApplicationType.Individual}
                                onChange={updateApplicationClients}
                                options={[
                                  { label: ApplicationType.Individual, value: ApplicationType.Individual },
                                  { label: ApplicationType.Joint, value: ApplicationType.Joint },
                                ]}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <TextInput label="Account nickname e.g. Education fund" name={`${key}.nickname`} value={application.nickname} />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Select name={`${key}.investmentPurpose`} label="Investment purpose" options={investmentPurposeOptions} hideValidation={otherSelected} />
                            </Grid>
                            {otherSelected && (
                              <Grid item md={6} xs={12}>
                                <TextInput name={`${key}.investmentPurpose`} label="Reason" value={application.investmentPurpose} />
                              </Grid>
                            )}
                            <Grid item md={6} xs={12}>
                              <Select name={`${key}.investmentMethods`} label="Investment method" options={investmentMethodOptions} />
                            </Grid>
                            {isJoint && (
                              <>
                                <Grid item md={6} xs={12}>
                                  <Select name={`${key}.annualIncome`} label="Estimated net annual income" options={incomeBands} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <Select
                                    name={`${key}.estimatedTotalWealth`}
                                    label="Estimated net wealth"
                                    tooltip="Please estimate the net wealth of all account holders personal wealth (excluding property and business assets)."
                                    options={wealthBands}
                                  />
                                </Grid>
                              </>
                            )}
                            {showInvestmentOptions && (
                              <Grid item md={6} xs={12}>
                                <FormLabel label="Investment options" />
                                <InvestmentOption horizontal namePrefix={key} value={application as WealthBuilderApplicationDto} validationKey="wbTotalAllocated" />
                              </Grid>
                            )}
                            {isJoint && (
                              <>
                                <Grid item xs={12}>
                                  <SubtitleText hideMarginBottom>Enter details of the joint applicant.</SubtitleText>
                                </Grid>
                                <Grid item xs={12}>
                                  <Checkbox label="The joint applicant is over the age of 16" name={`${key}.applicationClients[${applicationClientIndex}].overMinAge`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput placeholder="First name as per client's ID" label="First name" name={`${key}.applicationClients[${applicationClientIndex}].firstName`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput placeholder="Middle name as per client's ID" label="Middle name(s)" name={`${key}.applicationClients[${applicationClientIndex}].middleName`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput placeholder="Last name as per client's ID" label="Last name" name={`${key}.applicationClients[${applicationClientIndex}].lastName`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput label="Email" type="email" name={`${key}.applicationClients[${applicationClientIndex}].email`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput label="Mobile" type="tel" name={`${key}.applicationClients[${applicationClientIndex}].mobile`} />
                                </Grid>
                                {Object.keys(errors).includes("jointApplicant") && (
                                  <Grid item xs={12}>
                                    <ErrorMessage name="jointApplicant" />
                                  </Grid>
                                )}
                              </>
                            )}
                            {index + 1 === values.wealthBuilderApplications.length && (
                              <Grid item md={(showInvestmentOptions && !otherSelected) || (!showInvestmentOptions && otherSelected) || isJoint ? 6 : 12} xs={12}>
                                <Grid container alignItems="flex-end" justifyContent="flex-end" style={{ height: "100%" }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={(_) =>
                                      arrayHelpers.push({
                                        active: true,
                                        nickname: "",
                                        product: Product.WealthBuilder,
                                        investmentPurpose: undefined,
                                        investmentMethods: undefined,
                                        estimatedTotalWealth: undefined,
                                        annualIncome: undefined,
                                        investmentOption: undefined,
                                      })
                                    }
                                  >
                                    Add Wealth Builder
                                  </Button>
                                </Grid>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      );
                    })
                }
              />
            )}
            {values.products?.includes(Product.IncomeGenerator) && (
              <FieldArray
                name="incomeGeneratorApplications"
                render={(arrayHelpers) =>
                  values.incomeGeneratorApplications
                    .filter((a) => a.active)
                    .map((application, index) => {
                      const key = `incomeGeneratorApplications[${index}]`;
                      const isJoint = isJointAccount(application);
                      const applicationClientIndex = application.applicationClients?.findIndex((client) => client.role === ApplicationClientRoles.Joint);
                      const otherSelected =
                        application.investmentPurpose === "" || (!!application.investmentPurpose && !investmentPurposeOptions.find((p) => p.value === application.investmentPurpose));

                      const updateApplicationClients = (applicationType: ApplicationType) => {
                        if (applicationType === ApplicationType.Individual) {
                          setFieldValue(
                            `${key}.applicationClients`,
                            application.applicationClients?.filter((client) => client.role === ApplicationClientRoles.Primary)
                          );
                          setFieldValue(`${key}.annualIncome`, client?.annualIncome);
                          setFieldValue(`${key}.estimatedTotalWealth`, client?.estimatedTotalWealth);
                        } else {
                          setFieldValue(
                            `${key}.applicationClients`,
                            ((application.applicationClients || []) as ApplicationClient[]).concat({
                              role: ApplicationClientRoles.Joint,
                              firstName: "",
                              middleName: "",
                              lastName: "",
                              mobile: "",
                              email: "",
                              overMinAge: false,
                            })
                          );
                          setFieldValue(`${key}.annualIncome`, undefined);
                          setFieldValue(`${key}.estimatedTotalWealth`, undefined);
                        }
                      };

                      return (
                        <Box mb={3} key={application.applicationId || index}>
                          <SectionTitleText>
                            <Grid container justifyContent="space-between">
                              <Grid item>{`Income Generator${application.nickname && ` - ${application.nickname}`}`}</Grid>
                              {index > 0 && (
                                <Grid item>
                                  <IconButton color="inherit" size="small" onClick={(_) => arrayHelpers.remove(index)}>
                                    <ClearIcon />
                                  </IconButton>
                                </Grid>
                              )}
                            </Grid>
                          </SectionTitleText>
                          <Grid container spacing={3}>
                            <Grid item md={6} xs={12}>
                              <RadioList
                                horizontal
                                label="Account type"
                                name={`${key}.applicationType`}
                                value={isJoint ? ApplicationType.Joint : ApplicationType.Individual}
                                onChange={updateApplicationClients}
                                options={[
                                  { label: ApplicationType.Individual, value: ApplicationType.Individual },
                                  { label: ApplicationType.Joint, value: ApplicationType.Joint },
                                ]}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <TextInput label="Account nickname e.g. Education fund" name={`${key}.nickname`} value={application.nickname} />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Select name={`${key}.investmentPurpose`} label="Investment purpose" options={investmentPurposeOptions} hideValidation={otherSelected} />
                            </Grid>
                            {otherSelected && (
                              <Grid item md={6} xs={12}>
                                <TextInput name={`${key}.investmentPurpose`} label="Reason" value={application.investmentPurpose} />
                              </Grid>
                            )}
                            <Grid item md={6} xs={12}>
                              <Select name={`${key}.investmentMethods`} label="Investment method" options={investmentMethodOptions} />
                            </Grid>
                            {isJoint && (
                              <>
                                <Grid item md={6} xs={12}>
                                  <Select name={`${key}.annualIncome`} label="Estimated net annual income" options={incomeBands} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <Select
                                    name={`${key}.estimatedTotalWealth`}
                                    label="Estimated net wealth"
                                    tooltip="Please estimate the net wealth of all account holders personal wealth (excluding property and business assets)."
                                    options={wealthBands}
                                  />
                                </Grid>
                              </>
                            )}
                            <Grid item md={6} xs={12}>
                              <FormLabel label="Distribution options" />
                              <RadioList horizontal name={`${key}.distributionOption`} options={distributionOptionOptions} />
                            </Grid>
                            {isJoint && (
                              <>
                                <Grid item xs={12}>
                                  <SubtitleText hideMarginBottom>Enter details of the joint applicant.</SubtitleText>
                                </Grid>
                                <Grid item xs={12}>
                                  <Checkbox label="The joint applicant is over the age of 16" name={`${key}.applicationClients[${applicationClientIndex}].overMinAge`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput label="First name" name={`${key}.applicationClients[${applicationClientIndex}].firstName`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput label="Middle name(s)" name={`${key}.applicationClients[${applicationClientIndex}].middleName`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput label="Last name" name={`${key}.applicationClients[${applicationClientIndex}].lastName`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput label="Email" type="email" name={`${key}.applicationClients[${applicationClientIndex}].email`} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextInput label="Mobile" type="tel" name={`${key}.applicationClients[${applicationClientIndex}].mobile`} />
                                </Grid>
                                {Object.keys(errors).includes("jointApplicant") && (
                                  <Grid item xs={12}>
                                    <ErrorMessage name="jointApplicant" />
                                  </Grid>
                                )}
                              </>
                            )}
                            {index + 1 === values.incomeGeneratorApplications?.length && (
                              <Grid item md={!otherSelected || isJoint ? 6 : 12} xs={12}>
                                <Grid container alignItems="flex-end" justifyContent="flex-end" style={{ height: "100%" }}>
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={(_) =>
                                      arrayHelpers.push({
                                        active: true,
                                        nickname: "",
                                        product: Product.IncomeGenerator,
                                        investmentPurpose: undefined,
                                        investmentMethods: undefined,
                                        estimatedTotalWealth: undefined,
                                        annualIncome: undefined,
                                        distributionOption: undefined,
                                      })
                                    }
                                  >
                                    Add Income Generator
                                  </Button>
                                </Grid>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      );
                    })
                }
              />
            )}
            {!isBnzBankAccount(values?.bankAccountNumber) && <SelectIdentification inBrowserApp={false} />}
            <Grid container spacing={2} justifyContent="space-between">
              <Grid item>
                <Button variant="contained" type="submit" disabled={formMode !== FormMode.None} onClick={() => setPrevScreen(true)}>
                  Previous
                </Button>
              </Grid>
              <Grid item>
                {client?.adviceDelivery === AdviceDelivery.InPerson && hasSelectedProductRoles(user, values?.products) && (
                  <Button variant="contained" type="button" color="primary" disabled={formMode !== FormMode.None} onClick={() => isValid && openSendToClient(values)}>
                    Send to client
                  </Button>
                )}
                {(values.kiwiSaverApplication?.isEligible !== false || !!values.products?.includes(Product.WealthBuilder) || !!values.products?.includes(Product.IncomeGenerator)) &&
                  client?.adviceDelivery !== AdviceDelivery.InPerson && (
                    <Button variant="contained" color="primary" type="submit">
                      Next
                    </Button>
                  )}
              </Grid>
            </Grid>
          </>
        )}
      </FormikWrapper>
      <SendApplicationToClient hasMobile={!!client?.mobile} open={formMode === FormMode.Confirm} cancel={() => setFormMode(FormMode.None)} ok={sendToClient} />
    </>
  );
};

export default ProductDetails;
